import { REGEX_MATCH } from "@/specs/validations-2";
import { contactSamples, thirdPartyDestroyerSamples, wholesalerSamples, notesSamples } from "../sample-data";
import { CLM } from "./claims";
import { ARE_EQUAL, dateOnly, dateOnly_FROM_date, dateTime, decimal, Editable, editable, int, IS, map, money, text, unexpectedCodePath } from "@/fx/data-helpers";
import { addDays } from "date-fns";
import assert from "assert";
import { testProducts } from "../test-data";
import { SYS } from "./system";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const MILLISECONDS_IN_DAY = 1000 // milliseconds
                          * 60   // seconds
                          * 60   // minutes
                          * 24;  // hours

export const YES = "YES";
export const NO = "NO";

// both min and max are inclusive
export function randomIntRange(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
export function randomDecimalRange(min: number, max: number, places: number): number {
  return Math.floor((Math.random() * (max - min) + min) * 10 ** places) / 10 ** places;
}

export const randomIntRange_       = (min: number, max: number                ) => int            (randomIntRange    (min, max        ));
export const randomDecimalRange_   = (min: number, max: number, places: number) => decimal(places)(randomDecimalRange(min, max, places));
export const randomMoneyRange_     = (min: number, max: number                ) => money          (randomDecimalRange(min, max, 2     ));

// min = 0 and max is not inclusive
export const randomInt       = (max:    number) => Math.floor(Math.random() * max);
export const randomPaddedInt = (places: number) => randomInt(10 ** places).toString().padStart(places, "0");
export const randomStrInt    = (length: number) => randomInt(10 ** length).toString();
export const randomCapitals  = (length: number) => Array.from({ length }, () => ALPHABET[randomInt(ALPHABET.length)]).join("");

export const randomInt_       = (max:    number) => int(randomInt       (max   ));
export const randomPaddedInt_ = (places: number) => text(randomPaddedInt(places));
export const randomStrInt_    = (length: number) => text(randomStrInt   (length));
export const randomCapitals_  = (length: number) => text(randomCapitals (length));

export const randomBoolean           = () => randomYesNo() === YES;

// export const randomBoolean_          = () => bool(randomBoolean());

export const today = (days: number = 0) => {
  const now = new Date();
  return dateOnly_FROM_date(
    addDays(now, days)
  );
}

const stripTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const convertToDateOnly = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const randomDateOnly = (min: Date, max: Date) => {
  assert(IS.dateOnly(min), "min must be a date without time");
  assert(IS.dateOnly(max), "max must be a date without time");
  assert(min.getTime() <= max.getTime(), "min must be less than or equal to max");

  const daysDiff = (max.getTime() - min.getTime()) / (MILLISECONDS_IN_DAY);
  const randomDays = randomIntRange(0, daysDiff);
  return new Date(min.getTime() + randomDays * MILLISECONDS_IN_DAY);
};
export const randomDateOnly_ = (min: Date, max: Date) => dateOnly(randomDateOnly(min, max));

export const randomDateTime = (min: Date, max: Date) => {
  assert(min.getTime() <= max.getTime(), "min must be less than or equal to max");
  return new Date(randomIntRange(min.getTime(), max.getTime()));
}
export const randomDateTime_ = (min: Date, max: Date) => dateTime(randomDateTime(min, max));

// export const randomDate_ = (min: Date, max: Date) => dateOnly(randomDate(min, max).toISOString());

export const randomCases            = () => randomIntRange    (1, 10000);
export const randomBarrels          = () => randomDecimalRange(1, 10000, 5);
export const randomTax              = () => randomDecimalRange(1, 10000, 2);
export const randomRetailPercentage = () => randomIntRange(1, 100);

export const randomFileName = () => randomSingleFrom([
  "chat_with_steve.txt",
  "approval.png",
  "meeting_notes.docx",
  "presentation.pptx",
  "report.pdf",
  "invoice.xlsx",
  "contract.docx",
  "specification.pdf",
  "design.png",
  "proposal.docx"
]);

export const randomFileName_ = () => text(randomFileName());

export const randomNote = () => randomSingleFrom([...notesSamples, undefined]);

export const randomDestructionMethod = () => randomSingleFrom([
  "Recycling",
  "Landfill",
  "Incineration",
  "Kill-O-Zap Gun",
]);

export const randomDestructionMethod_ = () => text(randomDestructionMethod());

export const CLAIM_NUMBER      = REGEX_MATCH(/^C[0-9]{2}-[0-9]{4}$/, "Invalid number format");
export const SAP_ORDER_NUMBER  = REGEX_MATCH(/^[0-9]{8}$/,           "Invalid number format");
export const WHOLESALER_SAP_ID = REGEX_MATCH(/^[0-9A-Z]{6}$/,        "Invalid number format");

export const normalize_sapOrderNumber = (_: Editable) => editable(_.toUpperCase());
export const normalize_wholesalerId   = (_: Editable) => editable(_.toUpperCase());

export const randomClaimNumber     = () => "C24-" + randomPaddedInt(4);
export const randomSapOrderNumber  = () => "6008" + randomPaddedInt(4);
export const randomWholesalerSapId = () => randomPaddedInt(3) + randomCapitals(3);

export const randomClaimNumber_     = () => text(randomClaimNumber    ());
export const randomSapOrderNumber_  = () => text(randomSapOrderNumber ());
export const randomWholesalerSapId_ = () => text(randomWholesalerSapId());

export const yesNo = [
  { label: "Yes", value: YES },
  { label: "No",  value: NO  },
];
export const toYesNo   = (_?: boolean) => _ === undefined ? undefined : _ ? YES : NO;
export const fromYesNo = (_?: string ) => _ === undefined ? undefined : _ === YES;

// TODO: consolidate fields to embrace enums
export const beverageTypes = [
  { label: "Beer / Flavored Malt Beverages (FMB) Cases", value: "BEER_FMB",       enum: CLM.BeverageType.BEER_FMB       },
  { label: "Cider Cases",                                value: "CIDER",          enum: CLM.BeverageType.CIDER          },
  { label: "Spirits Cases",                              value: "SPIRITS",        enum: CLM.BeverageType.SPIRITS        },
  { label: "Non-Alcoholic Cases",                        value: "NON_ALCOHOLIC",  enum: CLM.BeverageType.NON_ALCOHOLIC  },
];
export const containers = [
  { label: "Bottles",          value: "BOTTLES",          enum: CLM.Container.BOTTLES     },
  { label: "Cans",             value: "CANS",             enum: CLM.Container.CANS        },
  { label: "Bag",              value: "BAG",              enum: CLM.Container.BAG         },
  { label: "Freeze Pops",      value: "FREEZE_POPS",      enum: CLM.Container.FREEZE_POPS },
];
export const destructionReasons = [
  { label: "Out of Code",      value: "OUT_OF_CODE",      enum: CLM.DestructionReason.OUT_OF_CODE,      weight: 99 },
  { label: "Out of Code Club", value: "OUT_OF_CODE_CLUB", enum: CLM.DestructionReason.OUT_OF_CODE_CLUB, weight: 1  },
];
export const creditPercentages = [
  { label: '5%',   value: 5,   weight: 1  },
  { label: '10%',  value: 10,  weight: 1  },
  { label: '15%',  value: 15,  weight: 1  },
  { label: '20%',  value: 20,  weight: 1  },
  { label: '25%',  value: 25,  weight: 1  },
  { label: '30%',  value: 30,  weight: 1  },
  { label: '35%',  value: 35,  weight: 1  },
  { label: '40%',  value: 40,  weight: 1  },
  { label: '45%',  value: 45,  weight: 1  },
  { label: '50%',  value: 50,  weight: 15 },
  { label: '55%',  value: 55,  weight: 1  },
  { label: '60%',  value: 60,  weight: 1  },
  { label: '65%',  value: 65,  weight: 1  },
  { label: '70%',  value: 70,  weight: 1  },
  { label: '75%',  value: 75,  weight: 1  },
  { label: '80%',  value: 80,  weight: 1  },
  { label: '85%',  value: 85,  weight: 1  },
  { label: '90%',  value: 90,  weight: 1  },
  { label: '95%',  value: 95,  weight: 1  },
  { label: '100%', value: 100, weight: 75 },
];
export const managerLevels_flca = [
  { label: "Market Manager",     value: "MARKET_MANAGER",     enum: CLM.ManagerLevel.MARKET_MANAGER     },
  { label: "District Manager",   value: "DISTRICT_MANAGER",   enum: CLM.ManagerLevel.DISTRICT_MANAGER   },
  { label: "Area Manager",       value: "AREA_MANAGER",       enum: CLM.ManagerLevel.AREA_MANAGER       },
  { label: "Regional Manager",   value: "REGIONAL_MANAGER",   enum: CLM.ManagerLevel.REGIONAL_MANAGER   },
  { label: "Division Director",  value: "DIVISION_DIRECTOR",  enum: CLM.ManagerLevel.DIVISION_DIRECTOR  },
  { label: "Divisional Analyst", value: "DIVISIONAL_ANALYST", enum: CLM.ManagerLevel.DIVISIONAL_ANALYST },
];
export const managerLevels_ulca = [
  { label: "Regional Manager",                                  value: "REGIONAL_MANAGER",                                  enum: CLM.ManagerLevel.REGIONAL_MANAGER                                  },
  { label: "Division Director",                                 value: "DIVISION_DIRECTOR",                                 enum: CLM.ManagerLevel.DIVISION_DIRECTOR                                 },
  { label: "Chief Sales Officer",                               value: "CHIEF_SALES_OFFICER",                               enum: CLM.ManagerLevel.CHIEF_SALES_OFFICER                               },
  { label: "Customer and Supply Logistics Management Director", value: "CUSTOMER_AND_SUPPLY_LOGISTICS_MANAGEMENT_DIRECTOR", enum: CLM.ManagerLevel.CUSTOMER_AND_SUPPLY_LOGISTICS_MANAGEMENT_DIRECTOR },
];
export const destructionCompanyTypes = [
  { label: "Wholesaler",                  value: "WHOLESALER",  enum: CLM.DestructionCompanyType.WHOLESALER    },
  { label: "Another company (3rd party)", value: "THIRD_PARTY", enum: CLM.DestructionCompanyType.THIRD_PARTY  },
];
export const destructionCompanyTypes_forDistributors = [
  { label: "Distributor/Onsite",          value: "WHOLESALER",  enum: CLM.DestructionCompanyType.WHOLESALER    },
  { label: "Another company (3rd party)", value: "THIRD_PARTY", enum: CLM.DestructionCompanyType.THIRD_PARTY  },
];
export const claimTypes = [
  { label: "Destruction", value: "DESTRUCTION", enum: CLM.ClaimType.DESTRUCTION },
  { label: "Overage",     value: "OVERAGE",     enum: CLM.ClaimType.OVERAGE     },
  { label: "Shortage",    value: "SHORTAGE",    enum: CLM.ClaimType.SHORTAGE    },
]
export const claimReasons = [
  { label: "Out-of-code product",     value: "OUT_OF_CODE",   enum: CLM.ClaimReason.OUT_OF_CODE   },
  { label: "Product is/was damaged",  value: "DAMAGED",       enum: CLM.ClaimReason.DAMAGED       },
  { label: "A product quality alert", value: "QUALITY_ALERT", enum: CLM.ClaimReason.QUALITY_ALERT },
  { label: "A product misshipment",   value: "MISSHIPMENT",   enum: CLM.ClaimReason.MISSHIPMENT   },
  { label: "Short-coded product",     value: "SHORT_CODED",   enum: CLM.ClaimReason.SHORT_CODED   },
  { label: "An over-shipment",        value: "OVERSHIPMENT",  enum: CLM.ClaimReason.OVERSHIPMENT  },
  { label: "An under-shipment",       value: "UNDERSHIPMENT", enum: CLM.ClaimReason.UNDERSHIPMENT },
]
export const states = [
  { label: "Alabama",        value: "AL", enum: CLM.State.AL },
  { label: "Alaska",         value: "AK", enum: CLM.State.AK },
  { label: "Arizona",        value: "AZ", enum: CLM.State.AZ },
  { label: "Arkansas",       value: "AR", enum: CLM.State.AR },
  { label: "California",     value: "CA", enum: CLM.State.CA },
  { label: "Colorado",       value: "CO", enum: CLM.State.CO },
  { label: "Connecticut",    value: "CT", enum: CLM.State.CT },
  { label: "Delaware",       value: "DE", enum: CLM.State.DE },
  { label: "Florida",        value: "FL", enum: CLM.State.FL },
  { label: "Georgia",        value: "GA", enum: CLM.State.GA },
  { label: "Hawaii",         value: "HI", enum: CLM.State.HI },
  { label: "Idaho",          value: "ID", enum: CLM.State.ID },
  { label: "Illinois",       value: "IL", enum: CLM.State.IL },
  { label: "Indiana",        value: "IN", enum: CLM.State.IN },
  { label: "Iowa",           value: "IA", enum: CLM.State.IA },
  { label: "Kansas",         value: "KS", enum: CLM.State.KS },
  { label: "Kentucky",       value: "KY", enum: CLM.State.KY },
  { label: "Louisiana",      value: "LA", enum: CLM.State.LA },
  { label: "Maine",          value: "ME", enum: CLM.State.ME },
  { label: "Maryland",       value: "MD", enum: CLM.State.MD },
  { label: "Massachusetts",  value: "MA", enum: CLM.State.MA },
  { label: "Michigan",       value: "MI", enum: CLM.State.MI },
  { label: "Minnesota",      value: "MN", enum: CLM.State.MN },
  { label: "Mississippi",    value: "MS", enum: CLM.State.MS },
  { label: "Missouri",       value: "MO", enum: CLM.State.MO },
  { label: "Montana",        value: "MT", enum: CLM.State.MT },
  { label: "Nebraska",       value: "NE", enum: CLM.State.NE },
  { label: "Nevada",         value: "NV", enum: CLM.State.NV },
  { label: "New Hampshire",  value: "NH", enum: CLM.State.NH },
  { label: "New Jersey",     value: "NJ", enum: CLM.State.NJ },
  { label: "New Mexico",     value: "NM", enum: CLM.State.NM },
  { label: "New York",       value: "NY", enum: CLM.State.NY },
  { label: "North Carolina", value: "NC", enum: CLM.State.NC },
  { label: "North Dakota",   value: "ND", enum: CLM.State.ND },
  { label: "Ohio",           value: "OH", enum: CLM.State.OH },
  { label: "Oklahoma",       value: "OK", enum: CLM.State.OK },
  { label: "Oregon",         value: "OR", enum: CLM.State.OR },
  { label: "Pennsylvania",   value: "PA", enum: CLM.State.PA },
  { label: "Rhode Island",   value: "RI", enum: CLM.State.RI },
  { label: "South Carolina", value: "SC", enum: CLM.State.SC },
  { label: "South Dakota",   value: "SD", enum: CLM.State.SD },
  { label: "Tennessee",      value: "TN", enum: CLM.State.TN },
  { label: "Texas",          value: "TX", enum: CLM.State.TX },
  { label: "Utah",           value: "UT", enum: CLM.State.UT },
  { label: "Vermont",        value: "VT", enum: CLM.State.VT },
  { label: "Virginia",       value: "VA", enum: CLM.State.VA },
  { label: "Washington",     value: "WA", enum: CLM.State.WA },
  { label: "West Virginia",  value: "WV", enum: CLM.State.WV },
  { label: "Wisconsin",      value: "WI", enum: CLM.State.WI },
  { label: "Wyoming",        value: "WY", enum: CLM.State.WY },
];
export const productionLocations = [
  { label: "American Craft Brewery LLC, Breinigsville, PA  (BR-PA-SAM-15000)", value: "BR-PA-SAM-15000", enum: CLM.ProductionLocation["BR-PA-SAM-15000"]},
  { label: "American Craft Brewery LLC, Cincinnati, OH (BR-OH-SAM-1)",         value: "BR-OH-SAM-1",     enum: CLM.ProductionLocation["BR-OH-SAM-1"    ]},
  { label: "American Craft Brewery LLC, Latrobe, PA (BR-PA-BOS-15000)",        value: "BR-PA-BOS-15000", enum: CLM.ProductionLocation["BR-PA-BOS-15000"]},
  { label: "American Craft Brewery LLC, Memphis, TN (BR-TN-21054)",            value: "BR-TN-21054",     enum: CLM.ProductionLocation["BR-TN-21054"    ]},
  { label: "American Craft Brewery LLC, Irwindale, CA (BR-CA-21278)",          value: "BR-CA-21278",     enum: CLM.ProductionLocation["BR-CA-21278"    ]},
  { label: "Dogfish Head Craft Brewery LLC, Milton, DE (BR-DE-21015)",         value: "BR-DE-21015",     enum: CLM.ProductionLocation["BR-DE-21015"    ]},
  { label: "American Craft Brewery LLC, Los Angeles, CA (BR-CA-AME-15003)",    value: "BR-CA-AME-15003", enum: CLM.ProductionLocation["BR-CA-AME-15003"]},
  { label: "RAUCH North America, Inc. (BR-AZ-20145)",                          value: "BR-AZ-20145",     enum: CLM.ProductionLocation["BR-AZ-20145"    ]},
];

export const taxCreditEligibilityRisks = [
  { label: "Wholesaler destroyed product before TTB submission.",                                             value: "DESTROYED_BEFORE_TTB_SUBMISSION",                    enum: CLM.TaxCreditEligibilityRisk["DESTROYED_BEFORE_TTB_SUBMISSION"                   ] },
  { label: "Wholesaler destroyed product before TTB approval.",                                               value: "DESTROYED_BEFORE_TTB_APPROVAL",                      enum: CLM.TaxCreditEligibilityRisk["DESTROYED_BEFORE_TTB_APPROVAL"                     ] },
  { label: "Wholesaler destroyed product before TTB approval deadline.",                                      value: "DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE",             enum: CLM.TaxCreditEligibilityRisk["DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE"            ] },
  { label: "NOI submission to TTB was skipped.",                                                              value: "NOI_SUBMISSION_SKIPPED",                             enum: CLM.TaxCreditEligibilityRisk["NOI_SUBMISSION_SKIPPED"                                       ] },
  { label: "Wholesaler credited more than 30 days after destruction.",                                        value: "LATE_WHOLESALER_CREDIT",                             enum: CLM.TaxCreditEligibilityRisk["LATE_WHOLESALER_CREDIT"                            ] },
  { label: "AODs are incomplete and/or contain conflicting information.",                                     value: "INVALID_AODS",                                       enum: CLM.TaxCreditEligibilityRisk["INVALID_AODS"                                      ] },
  { label: "Skipped waiting for TTB approval and wholesaler destroyed product before TTB approval deadline.", value: "TTB_SKIPPED_DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE", enum: CLM.TaxCreditEligibilityRisk["TTB_SKIPPED_DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE"] },
];
export const aodIssues = [
  { label: "The claim’s NOI Number is missing or incorrect.",                  value: "INVALID_NOI",                  enum: CLM.AodIssue.INVALID_NOI                  },
  { label: "The destruction company does not match the NOI.",                  value: "INVALID_DESTRUCTION_COMPANY",  enum: CLM.AodIssue.INVALID_DESTRUCTION_COMPANY  },
  { label: "The destruction location does not match the NOI.",                 value: "INVALID_DESTRUCTION_LOCATION", enum: CLM.AodIssue.INVALID_DESTRUCTION_LOCATION },
  { label: "Signatures do not align with the destruction company.",            value: "INVALID_SIGNATURES",           enum: CLM.AodIssue.INVALID_SIGNATURES           },
  { label: "Other (see Affidavit Review Notes).",                              value: "OTHER",                        enum: CLM.AodIssue.OTHER                        },
];
export const issueTypes = [
  { label: "Claim information issue in this app", value: "APP__CLAIM_INFORMATION_ISSUE", enum: CLM.IssueType.APP__CLAIM_INFORMATION_ISSUE },
  { label: "Accounting data error in SAP",        value: "SAP__ACCOUNTING_DATA_ERROR",   enum: CLM.IssueType.SAP__ACCOUNTING_DATA_ERROR   },
  { label: "Claim data entry error in SAP",       value: "SAP__CLAIM_DATA_ENTRY_ERROR",  enum: CLM.IssueType.SAP__CLAIM_DATA_ENTRY_ERROR  },
];
export const userGroups = [
  { label: "Accounting", value: "BBC__WHOLESALER_CREDIT_ACCOUNTING",     enum: CLM.UserGroup.BBC__WHOLESALER_CREDIT_ACCOUNTING },
  { label: "Logistics",  value: "BBC__LOGISTICS",                        enum: CLM.UserGroup.BBC__LOGISTICS                    },
];
export const claimVerificationMethods = [
  { label: "In Person",         value: "IN_PERSON",         enum: CLM.ClaimVerificationMethod.IN_PERSON         },
  { label: "By Photo or Video", value: "BY_PHOTO_OR_VIDEO", enum: CLM.ClaimVerificationMethod.BY_PHOTO_OR_VIDEO },
];
export const distributorAcknowledgement = [
  { label: "By checking this box, I confirm this form has been completed accurately and in good faith and is in compliance with The Boston Beer Company Beer Return Policy (available at https://obo.bostonbeer.com/WebApp/Report/report_select.aspx).", value: "YES" },
];
export const resourceTypes = [
  { label: "Claim",       value: "CLAIMS_CLAIM",       enum: SYS.ResourceType.CLAIMS_CLAIM       },
  { label: "Distributor", value: "CLAIMS_DISTRIBUTOR", enum: SYS.ResourceType.CLAIMS_DISTRIBUTOR },
  { label: "Product",     value: "CLAIMS_PRODUCT",     enum: SYS.ResourceType.CLAIMS_PRODUCT     },
  { label: "User",        value: "SYSTEM_USER",        enum: SYS.ResourceType.SYSTEM_USER        },
  { label: "Process",     value: "SYSTEM_PROCESS",     enum: SYS.ResourceType.SYSTEM_PROCESS     },
];

export const aodIssues_WITHOUT_other = aodIssues.filter(_ => _.value !== CLM.AodIssue.OTHER.identifier);

export const randomYesNo                                   = () => randomSingleFrom(yesNo                                  ).value;
export const randomBeverageType                            = () => randomSingleFrom(beverageTypes                          ).value;
export const randomFlcaClaimApproverLevel                  = () => randomSingleFrom(managerLevels_flca                     ).value;
export const randomUlcaClaimApproverLevel                  = () => randomSingleFrom(managerLevels_ulca                     ).value;
export const randomDestructionCompanyType                  = () => randomSingleFrom(destructionCompanyTypes                ).value;
export const randomDestructionCompanyTypes_forDistributors = () => randomSingleFrom(destructionCompanyTypes_forDistributors).value;
export const randomState                                   = () => randomSingleFrom(states                                 ).value;
export const randomProductionLocation                      = () => randomSingleFrom(productionLocations                    ).value;
export const randomAodIssue                                = () => randomSingleFrom(aodIssues                              ).value;
export const randomIssueType                               = () => randomSingleFrom(issueTypes                             ).value;
export const randomUserGroup                               = () => randomSingleFrom(userGroups                             ).value;

export const randomYesNo_                  = () => text(randomYesNo                 ());
export const randomBeverageType_           = () => text(randomBeverageType          ());
export const randomFlcaClaimApproverLevel_ = () => text(randomFlcaClaimApproverLevel());
export const randomUlcaClaimApproverLevel_ = () => text(randomUlcaClaimApproverLevel());
export const randomDestructionCompanyType_ = () => text(randomDestructionCompanyType());
export const randomState_                  = () => text(randomState                 ());
export const randomProductionLocation_     = () => text(randomProductionLocation    ());
export const randomAodIssue_               = () => text(randomAodIssue              ());

export const randomBeverageType_enum           = () => randomSingleFrom        (beverageTypes          ).enum;
export const randomContainer_enum              = () => randomSingleFrom        (containers             ).enum;
export const randomDestructionCompanyType_enum = () => randomSingleFrom        (destructionCompanyTypes).enum;
export const randomDestructionReason_enum      = () => randomWeightedSingleFrom(destructionReasons     ).enum;

export const randomUlcaClaimApproverLevels = () => randomMultipleFrom(managerLevels_ulca .map(_ => _.value));
export const randomProductionLocations     = () => randomMultipleFrom(productionLocations.map(_ => _.value));
export const randomAodIssues               = () => randomMultipleFrom(aodIssues          .map(_ => _.value));

export const randomOptional = <TValue>(_: () => TValue) => randomBoolean() ? _() : undefined;

export const randomWholesaler     = () => randomSingleFrom(wholesalerSamples);
export const randomWholesalerName = () => randomWholesaler().name;

export const randomCreditPercentage = ({ destructionReason }: { destructionReason: CLM.DestructionReason }) => {
  if(ARE_EQUAL.enum(CLM.destructionReasons)(destructionReason, CLM.DestructionReason.OUT_OF_CODE_CLUB)) {
    return 100;
  }
  return randomWeightedSingleFrom(creditPercentages).value;
}

const randomProducts_ = ({ beverageType }: { beverageType: CLM.BeverageType }) => {
  const products = testProducts.filter((testProduct) => beverageType.identifier === testProduct.beverageType);
  return randomMultipleFrom(products, 1, 10);
}

const randomProduct_ = ({ beverageType }: { beverageType: CLM.BeverageType }) => {
  const products = randomProducts_({ beverageType });
  assert(products.length > 0, "No products found for the given beverage type " + beverageType.identifier);
  return randomSingleFrom(products);
}

export const randomProducts = ({ beverageType }: { beverageType: CLM.BeverageType }) => {
  const products = randomProducts_({ beverageType });
  return products.map(product => CLM.productReference(product.id, product.beverageType));
}

export const randomBeverageType_enum_FROM_testProducts = () => {
  const beverageType = randomSingleFrom(testProducts.map(product => product.beverageType));
  return CLM.beverageType(beverageType);
}

export const randomBrandFamily = ({ beverageType }: { beverageType: CLM.BeverageType }) => {
  return randomProduct_({ beverageType }).brandFamily;
}

export const randomBrandStyle = ({ beverageType }: { beverageType: CLM.BeverageType }) => {
  return randomProduct_({ beverageType }).brandStyle;
}

export const randomPackaging_type = ({ beverageType }: { beverageType: CLM.BeverageType }) => {
  return randomProduct_({ beverageType }).packaging_type;
}

export const randomSku6 = () => {
  return randomIntRange_(10000, 99999).toString() + randomCapitals_(1);
}

export const randomVerificationMethod_enum = () => randomSingleFrom(claimVerificationMethods).enum;

export const randomWholesaler_ = () => map(randomWholesaler(), _ => ({
  name:   text(_.name),
  domain: text(_.domain),
  street: text(_.street),
  city:   text(_.city),
  state:  text(_.state),
  zip:    text(_.zip),
  sapId:  text(_.sapId),
}));

export const randomThirdPartyDestroyer = () => randomSingleFrom(thirdPartyDestroyerSamples);

export const randomThirdPartyDestroyer_ = () => map(randomThirdPartyDestroyer(), _ => ({
  name:   text(_.name),
  domain: text(_.domain),
  street: text(_.street),
  city:   text(_.city),
  state:  text(_.state),
  zip:    text(_.zip),
}));

export const randomContact       = () => randomSingleFrom(contactSamples);
export const randomContactName   = () => randomContact().name;
export const randomContactEmail  = () => randomContact().email;

export const randomContact_ = () => map(randomContact(), _ => ({
  name:  text(_.name),
  email: text(_.email),
}));
export const randomContactName_  = () => text(randomContactName ());
export const randomContactEmail_ = () => text(randomContactEmail());

export const randomSingleFrom = <T>(array: T[]) => array[Math.floor(Math.random() * array.length)];
export const randomMultipleFrom = <T>(array: T[], minCount: number = 1, maxCount: number = array.length) => {
  const resultsCount = minCount + randomInt(maxCount - minCount + 1);
  const selection = Array.from<T>({ length: resultsCount }).reduce(
    acc => {
      const next = randomSingleFrom(array.filter(_ => !acc.includes(_)));
      return [...acc, next];
    },
    [] as T[],
  );
  return array.filter(_ => selection.includes(_));
}
export const randomWeightedSingleFrom = <T extends { weight?: number }>(array: T[]) => {
  const totalWeight = array.reduce((sum, item) => {
                                                    const weight = item.weight ?? 1;
                                                    assert(IS.int(weight) && weight >= 0, "Weight must be a non-negative integer");
                                                    return sum + weight;
                                                  }, 0);
  const randomValue = randomIntRange(0, totalWeight);
  let cumulativeWeight = 0;

  for (const item of array) {
    cumulativeWeight += item.weight ?? 1;
    if (randomValue <= cumulativeWeight) {
      return item;
    }
  }

  unexpectedCodePath('randomWeightedSingleFrom did not return an item with total weight ' + totalWeight + ' and random value ' + randomValue);
}
